import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Smartphone, Monitor } from 'lucide-react';
import { Button } from './ui/button';
import { PUBLIC_ROUTES } from '../utils/clientRoutes';

export default function PWAInstallPrompt({ darkMode = false }) {
  const { pathname } = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState('desktop');

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt if user hasn't dismissed it recently
      const lastDismissed = localStorage.getItem('pwaPromptDismissed');
      if (!lastDismissed || Date.now() - parseInt(lastDismissed) > 7 * 24 * 60 * 60 * 1000) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  // PW-1 — the install card is the staff app's. A trade who scanned a tag, a
  // client in a portal, a phone widget or a popped-out panel is not being
  // asked to put KEY-MADE on their home screen, and nor is anyone signed out.
  const path = pathname || '';
  const signedIn = !!(localStorage.getItem('accessToken') || localStorage.getItem('token'));
  if (!signedIn || PUBLIC_ROUTES.some((route) => path.startsWith(route))) return null;

  // Don't show if already installed or no prompt available (and not iOS)
  if (isInstalled) return null;
  if (!showPrompt && platform !== 'ios') return null;

  // iOS-specific instructions (Safari doesn't support beforeinstallprompt)
  if (platform === 'ios') {
    return (
      <div className={`fixed bottom-4 left-4 right-4 z-50 p-4 rounded-xl shadow-2xl border ${
        darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'
      }`}>
        <button 
          onClick={handleDismiss}
          className={`absolute top-2 right-2 p-1 rounded-full ${darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-100'}`}
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#BC5D41]/10">
            <Smartphone className="w-6 h-6 text-[#BC5D41]" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : ''}`}>
              Install KEY-MADE
            </h3>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
              Add to your home screen for quick access:
            </p>
            <ol className={`text-xs mt-2 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li>1. Tap the <strong>Share</strong> button <span className="inline-block w-4 h-4 align-middle">⬆️</span></li>
              <li>2. Scroll and tap <strong>&quot;Add to Home Screen&quot;</strong></li>
              <li>3. Tap <strong>Add</strong> to confirm</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Standard install prompt for Android/Desktop
  if (!showPrompt) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30`}>
      <div className={`relative w-full max-w-sm p-4 rounded-xl shadow-2xl border ${
        darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'
      }`}>
        <button 
          onClick={handleDismiss}
          className={`absolute top-2 right-2 p-1 rounded-full ${darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-100'}`}
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-[#BC5D41]/10">
            {platform === 'android' ? (
              <Smartphone className="w-6 h-6 text-[#BC5D41]" />
            ) : (
              <Monitor className="w-6 h-6 text-[#BC5D41]" />
            )}
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : ''}`}>
              Install KEY-MADE
            </h3>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
              {platform === 'android' 
                ? 'Add to your home screen for quick access and offline use.'
                : 'Install as an app for a better experience.'}
            </p>
            <div className="flex gap-2 mt-3">
              <Button 
                onClick={handleInstall}
                size="sm"
                className="bg-[#BC5D41] hover:bg-[#70170F] text-white"
              >
                <Download className="w-4 h-4 mr-1" />
                Install
              </Button>
              <Button 
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className={darkMode ? 'text-gray-400' : ''}
              >
                Not now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
